import { otto } from '@otto-ec/global-resources/nexus';
import type { SelectionApiTarget } from '@otto-ec/nexus-registry/selection';
import type { DresonRuleResolvedEvent, Page } from '@shared/types/Tilelist.ts';
import type { EventsIncoming } from '@tilelist/application/ports/EventsIncoming.ts';
import type { EventsOutgoing } from '@tilelist/application/ports/EventsOutgoing.ts';
import type {
    SelectionEvents,
    SelectionOperationContext,
    SelectionPage,
    SelectionState,
} from '@tilelist/application/ports/SelectionEvents.ts';

type LegacyContext = { initialEntryRule: string; currentUrl: () => string };

export function createSelectionEvents(
    incoming: EventsIncoming,
    outgoing: EventsOutgoing,
    enabled: boolean,
    context: LegacyContext,
): SelectionEvents {
    return enabled ? createSelectionClient() : createLegacySelectionEvents(incoming, outgoing, context);
}

export function createLegacySelectionEvents(
    incoming: EventsIncoming,
    outgoing: EventsOutgoing,
    context: LegacyContext,
): SelectionEvents {
    return {
        onUpdate(handler) {
            return incoming.onPageResolved((event) =>
                handler({
                    selection: fromLegacy(event, context),
                    ...(event.historic !== undefined ? { historic: event.historic } : {}),
                    ...(event.shouldScroll !== undefined ? { shouldScroll: event.shouldScroll } : {}),
                }),
            );
        },
        async assign(input, options) {
            const operationId = options?.operationId || createOperationId();
            outgoing.requestPageResolution({
                rule: input.rule,
                page: toLegacyPage(input.page),
                redirect: input.url,
                historic: options?.context?.historic,
                shouldScroll: options?.context?.shouldScroll,
            });
            return { selection: { ...input, entryRule: input.entryRule || context.initialEntryRule }, operationId };
        },
    };
}

export function fromLegacy(event: DresonRuleResolvedEvent, context: LegacyContext): SelectionState {
    const page = fromLegacyPage(event.page);
    return {
        rule: event.rule,
        page,
        url: canonicalSelectionUrl(event.redirect || context.currentUrl(), page),
        entryRule: context.initialEntryRule,
    };
}

export function fromLegacyPage(page: Page): SelectionPage {
    return {
        ...(page.sortiertnach !== undefined ? { sortOrder: page.sortiertnach } : {}),
        ...(page.o !== undefined ? { offset: page.o } : {}),
        ...(page.l !== undefined ? { layout: page.l } : {}),
        ...(page.c !== undefined ? { comparisonId: page.c } : {}),
    };
}

export function toLegacyPage(page: SelectionPage): Page {
    return {
        ...(page.sortOrder !== undefined ? { sortiertnach: page.sortOrder } : {}),
        ...(page.offset !== undefined ? { o: page.offset } : {}),
        ...(page.layout !== undefined ? { l: page.layout } : {}),
        ...(page.comparisonId !== undefined ? { c: page.comparisonId } : {}),
    };
}

export function canonicalSelectionUrl(rawUrl: string, page: SelectionPage): string {
    const url = new URL(rawUrl, window.location.origin);

    /*                                                                        */
    /*                                                           */
    /*                                                                         */
    /*                                                                         */
    /*                                                                           */
    /*                                                                    */
    const pageKeys = new Set(['sortiertnach', 'o', 'l', 'c']);
    const preserved = url.search
        .replace(/^\?/, '')
        .split('&')
        .filter((chunk) => chunk !== '' && !pageKeys.has(chunk.split('=')[0]));

    /*                                                                       */
    /*                                                                        */
    /*                                */
    const canonical = [...preserved];
    if (page.offset && page.offset !== '0' && page.layout) canonical.push(`l=${page.layout}`);
    if (page.offset && page.offset !== '0') canonical.push(`o=${page.offset}`);
    if (page.sortOrder && page.sortOrder !== 'topseller') canonical.push(`sortiertnach=${page.sortOrder}`);
    /*                                                                      */
    /*                                                                     */
    if (page.offset && page.offset !== '0' && page.comparisonId) {
        canonical.push(`c=${page.comparisonId}`);
    }

    const search = canonical.length > 0 ? `?${canonical.join('&')}` : '';
    return `${url.pathname}${search}${url.hash}`;
}

function createOperationId(): string {
    return `dundee-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 *
 *
 *
 *
 *
 *
 */
function createSelectionClient(target: SelectionApiTarget = otto.selection): SelectionEvents {
    const pending = new Map<string, { context?: SelectionOperationContext }>();

    return {
        onUpdate(handler) {
            const unsubscribeCurrent = target.current.on(
                (selection) => {
                    if (pending.size > 0) return;
                    return handler({ selection });
                },
                { skipRetained: true },
            );
            const unsubscribeChanges = target.changes.on((change) => {
                const operation = pending.get(change.operationId);
                pending.delete(change.operationId);
                return handler({
                    selection: change.selection,
                    operationId: change.operationId,
                    ...(operation?.context && typeof operation.context === 'object' ? operation.context : {}),
                    ...(operation?.context !== undefined ? { context: operation.context } : {}),
                });
            });

            return () => {
                unsubscribeCurrent();
                unsubscribeChanges();
            };
        },
        async assign(input, options) {
            const operationId = options?.operationId || createOperationId();
            pending.set(operationId, { context: options?.context });
            try {
                const result = await target.assign(input, { operationId });
                if (result.count === 0) pending.delete(operationId);
                return result;
            } catch (error) {
                pending.delete(operationId);
                throw error;
            }
        },
    };
}
